/**
 * @file Defines the route structure for the application.
 * Handles public, protected, and fallback routes using React Router.
 */
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import api from "./lib/axios";
import { lazy, Suspense } from "react";
import Layout from "./pages/Layout";
import Dashboard from "./pages/Dashboard";
import SeekAndDestroy from "./pages/SeekAndDestroy";
import Settings from "./pages/Settings";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import PrivateRoute from "./pages/components/PrivateRoute";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyReminder from "./pages/VerifyReminder";
import Onboarding from "./pages/Onboarding";
import OrgProfile from "./pages/OrgProfile";



const Help = lazy(() => import("./pages/Help"));

/** Public paths that should NEVER trigger an auth/me check */
const PUBLIC_PATHS = new Set<string>([
  "/", "/signup",
  "/verify-email", "/verify-reminder",
  "/forgot-password", "/reset-password",
  "/onboarding",
]);

/** Only runs auth check on protected pages. Never on PUBLIC_PATHS. */
function GlobalAuthGate() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const path = location.pathname;
    if (PUBLIC_PATHS.has(path)) return; // ✅ allow public pages (like /verify-email)

    (async () => {
      try {
        await api.get("/auth/me");
      } catch (err: any) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        console.log("🔐 GlobalAuthGate blocked", { status, detail, path: location.pathname });

        if (status === 403 && detail?.code === "EMAIL_NOT_VERIFIED") {
          navigate("/verify-reminder", { state: { email: detail.email } });
        } else if (status === 401) {
          navigate("/"); // not logged in
        } else {
          // Don’t punt to login on random 403/500 — just leave the page
          // (you can show a toast instead if you want)
        }
      }
    })();
  }, [location.pathname, navigate]);

  return null;
}

/**
 * The root routing component for the application.
 * Defines public and protected routes using React Router v6.
 */
export default function App() {
  return (
    <>
      <GlobalAuthGate />
      <Routes>
        {/* 🔓 Public Routes */}
        <Route path="/" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-reminder" element={<VerifyReminder />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding" element={<Onboarding />} />   {/* ✅ top-level */}

        {/* 🔐 Protected Routes */}
        <Route
          path="/app"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="seek-and-destroy" element={<SeekAndDestroy />} />
          <Route path="settings" element={<Settings />} />
          <Route
                  path="/app/help"
                  element={
                    <Suspense fallback={<div className="p-10">Loading…</div>}>
                      <Help />
                    </Suspense>
                  }
                />
          <Route path="settings/org-profile" element={<OrgProfile />} />
        </Route>

        {/* 🧼 Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}